import { ALLOW_FILE_TYPES } from '@exam-center/proctor-request-management';
import { ID_CARD_NUMBER_MASK } from '@exam-center/utils';
import { thaiTitleOptionSchema, TITLE_REQUIRED_ERROR_MESSAGE } from '@shared/utils';
import IMask from 'imask';
import { z } from 'zod';
const MIN_REQUIRED_FIELD_LENGTH = 1;
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 256;
const MAX_FILE_LENGTH = 4;
const MAX_UPLOAD_SIZE = 1024 * 1024 * 20;
const NAME_TH_PATTERN = /^[\u0E00-\u0E7F\s]+$/;

const signedUrlSchema = z.object({
  url: z.string().url(),
  name: z.string(),
  key: z.string(),
  type: z.string(),
  size: z.number(),
});

export type SignedUrlInput = z.infer<typeof signedUrlSchema>;

const fileSchema = z
  .instanceof(File)
  .refine(
    (file) => ALLOW_FILE_TYPES.includes(file.type),
    'รูปแบบไฟล์ไม่ถูกต้อง กรุณาอัปโหลดไฟล์ .jpg หรือ .jpeg หรือ .pdf เท่านั้น',
  );

const attachedFileSchema = z.array(z.union([signedUrlSchema, fileSchema]).nullable()).nullable();

export const BaseCreateThirdProctorModalFormSchema = z
  .object({
    personal: z
      .object({
        idNumber: z.string().nullable(),
        titleTh: thaiTitleOptionSchema.nullable(),
        firstNameTh: z.string().nullable(),
        lastNameTh: z.string().nullable(),
        email: z.string().nullable(),
      })
      .nullable(),
    document: z
      .object({
        agreementForms: attachedFileSchema,
        identifications: attachedFileSchema,
      })
      .nullable(),
  })
  .superRefine((data, ctx) => {
    const idNumber = data.personal?.idNumber?.trim();
    if (!idNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'idNumber'],
        message: 'กรุณากรอกเลขบัตรประชาชน',
      });
    } else {
      const mask = IMask.createMask({ mask: ID_CARD_NUMBER_MASK });
      mask.resolve(idNumber);
      if (!mask.isComplete) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['personal', 'idNumber'],
          message: 'รูปแบบเลขบัตรประชาชนไม่ถูกต้อง ต้องมีจำนวน 13 หลักและเป็นตัวเลขเท่านั้น',
        });
      }
    }
    if (!data.personal?.titleTh?.value || !data.personal?.titleTh?.label) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'titleTh'],
        message: TITLE_REQUIRED_ERROR_MESSAGE,
      });
    }
    const firstNameTh = data.personal?.firstNameTh?.trim() ?? '';
    if (!firstNameTh) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'firstNameTh'],
        message: 'กรุณากรอกชื่อภาษาไทย',
      });
    }
    if (!NAME_TH_PATTERN.test(firstNameTh)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'firstNameTh'],
        message: 'กรุณากรอกชื่อเป็นภาษาไทย',
      });
    }
    if (firstNameTh.length > MAX_NAME_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'firstNameTh'],
        message: `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`,
      });
    }
    const lastNameTh = data.personal?.lastNameTh?.trim() ?? '';
    if (!lastNameTh) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'lastNameTh'],
        message: 'กรุณากรอกนามสกุลภาษาไทย',
      });
    }
    if (!NAME_TH_PATTERN.test(lastNameTh)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'lastNameTh'],
        message: 'กรุณากรอกนามสกุลเป็นภาษาไทย',
      });
    }
    if (lastNameTh.length > MAX_NAME_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['personal', 'lastNameTh'],
        message: `ความยาวต้องไม่เกิน ${MAX_NAME_LENGTH} ตัวอักษร`,
      });
    }
    const email = data.personal?.email?.trim();
    if (email) {
      try {
        z.string()
          .max(MAX_EMAIL_LENGTH, `ความยาวต้องไม่เกิน ${MAX_EMAIL_LENGTH} ตัวอักษร`)
          .email({ message: 'รูปแบบอีเมลผู้ใช้ไม่ถูกต้อง' })
          .parse(email);
      } catch (e) {
        const issue = (e as z.ZodError).issues[0];
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['personal', 'email'],
          message: issue.message,
        });
      }
    }

    const validateFiles = (
      files: (SignedUrlInput | File | null)[] | null | undefined,
      path: string[],
    ) => {
      const items = files ?? [];
      const nonNullCount = items.filter((f) => f != null).length;

      if (nonNullCount < MIN_REQUIRED_FIELD_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: 'กรุณาแนบไฟล์อัปโหลด',
        });
        return;
      }
      if (nonNullCount > MAX_FILE_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `ไม่สามารถอัปโหลดได้เกิน ${MAX_FILE_LENGTH} ไฟล์`,
        });
      }
      const totalSize = items.reduce((acc, file) => acc + (file?.size ?? 0), 0);
      if (totalSize > MAX_UPLOAD_SIZE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message: `ขนาดไฟล์รวมกันต้องไม่เกิน ${MAX_UPLOAD_SIZE / (1024 * 1024)} MB`,
        });
      }
    };
    validateFiles(data.document?.agreementForms, ['document', 'agreementForms']);
    validateFiles(data.document?.identifications, ['document', 'identifications']);
  });

export type BaseCreateThirdProctorModalFormInput = z.input<
  typeof BaseCreateThirdProctorModalFormSchema
>;
